import { formatDateTimeShort } from "@/shared/lib/date-time";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Loader,
  ScrollArea,
} from "@devhop/ui";
import type { ShiftReport } from "../../domain/repo/get-shift-report";
import { useShiftReport } from "../api/queries";

type ShiftDetailDialogProps = {
  shiftId: string | null;
  branchId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function formatNum(n: number): string {
  return n.toLocaleString("lo-LA");
}

function ReportContent({ report }: { report: ShiftReport }) {
  const {
    shift,
    branchName,
    openedByName,
    closedByName,
    cashSummary,
    paymentSummary,
    orderSummary,
  } = report;
  const openedAtStr = shift.openedAt
    ? formatDateTimeShort(shift.openedAt)
    : "—";
  const closedAtStr = shift.closedAt
    ? formatDateTimeShort(shift.closedAt)
    : "—";

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-2 font-semibold text-muted-foreground text-sm">
          ຂໍ້ມູນກະ
        </h4>
        <dl className="space-y-1 text-sm">
          {branchName && (
            <>
              <dt className="text-muted-foreground">ສາຂາ</dt>
              <dd className="font-medium">{branchName}</dd>
            </>
          )}
          <dt className="text-muted-foreground">ເປີດໂດຍ</dt>
          <dd className="font-medium">{openedByName ?? shift.openedBy}</dd>
          <dt className="text-muted-foreground">ເປີດເວລາ</dt>
          <dd>{openedAtStr}</dd>
          <dt className="text-muted-foreground">ປິດໂດຍ</dt>
          <dd className="font-medium">
            {closedByName ?? shift.closedBy ?? "—"}
          </dd>
          <dt className="text-muted-foreground">ປິດເວລາ</dt>
          <dd>{closedAtStr}</dd>
          {shift.notes && (
            <>
              <dt className="text-muted-foreground">ໝາຍເຫດ</dt>
              <dd>{shift.notes}</dd>
            </>
          )}
        </dl>
      </div>

      <div>
        <h4 className="mb-2 font-semibold text-muted-foreground text-sm">
          ເງິນໃນລີ້ນຊັກ
        </h4>
        <ul className="space-y-1 text-sm">
          <li className="flex justify-between">
            <span className="text-muted-foreground">ເງິນຕົ້ນກະ</span>
            <span>{formatNum(cashSummary.openingFloat)} ₭</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">ເງິນຮັບ</span>
            <span>{formatNum(cashSummary.cashReceived)} ₭</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">ເງິນຄືນ</span>
            <span>-{formatNum(cashSummary.cashRefunded)} ₭</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">Pay In</span>
            <span>{formatNum(cashSummary.payIn)} ₭</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">Pay Out</span>
            <span>-{formatNum(cashSummary.payOut)} ₭</span>
          </li>
          <li className="flex justify-between font-medium">
            <span>ເງິນຄາດວ່າ</span>
            <span>{formatNum(cashSummary.expectedCash)} ₭</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">ເງິນນັບຈິງ</span>
            <span>{formatNum(cashSummary.closingAmount)} ₭</span>
          </li>
          <li
            className={`flex justify-between font-medium ${cashSummary.variance !== 0 ? (cashSummary.variance > 0 ? "text-green-700" : "text-destructive") : ""}`}
          >
            <span>ສ່ວນຕ່າງ</span>
            <span>{formatNum(cashSummary.variance)} ₭</span>
          </li>
        </ul>
      </div>

      {paymentSummary.length > 0 && (
        <div>
          <h4 className="mb-2 font-semibold text-muted-foreground text-sm">
            ການຈ່າຍແບ່ງຕາມວິທີ
          </h4>
          <ul className="space-y-1 text-sm">
            {paymentSummary.map((p) => (
              <li key={p.methodCode} className="flex justify-between">
                <span className="text-muted-foreground">
                  {p.methodDisplayName} ({p.type})
                </span>
                <span>{formatNum(p.grossAmount)} ₭</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="mb-2 font-semibold text-muted-foreground text-sm">
          ສະຫຼຸບ Order
        </h4>
        <ul className="space-y-1 text-sm">
          <li className="flex justify-between">
            <span className="text-muted-foreground">ຈຳນວນ Order ທີ່ຈ່າຍແລ້ວ</span>
            <span>{orderSummary.orderCount}</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">ຍອດກ່ອນຫັກສ່ວນຫຼຸດ</span>
            <span>{formatNum(orderSummary.grossBeforeDiscount)} ₭</span>
          </li>
          <li className="flex justify-between">
            <span className="text-muted-foreground">ສ່ວນຫຼຸດລວມ</span>
            <span className="text-green-700 dark:text-green-400">
              {orderSummary.totalDiscount > 0 ? "-" : ""}
              {formatNum(orderSummary.totalDiscount)} ₭
            </span>
          </li>
          <li className="flex justify-between border-t pt-2 font-medium">
            <span>ຍອດຂາຍສຸດທິ</span>
            <span>{formatNum(orderSummary.netSales)} ₭</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export function ShiftDetailDialog({
  shiftId,
  branchId,
  open,
  onOpenChange,
}: ShiftDetailDialogProps) {
  const {
    data: report,
    isLoading,
    error,
  } = useShiftReport(open ? shiftId : null, open ? branchId : null, {
    enabled: open && !!shiftId && !!branchId,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg">
        <DialogHeader>
          <DialogTitle>ລາຍລະອຽດກະ</DialogTitle>
        </DialogHeader>
        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader className="size-8 animate-spin text-muted-foreground" />
          </div>
        )}
        {error && (
          <p className="text-destructive text-sm">
            {error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດ"}
          </p>
        )}
        {report && !isLoading && (
          <ScrollArea className="max-h-[60vh] pr-4">
            <ReportContent report={report} />
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
